'use client'

import Image from 'next/image'
import {
  LuCheck,
  LuCreditCard,
  LuLandmark,
  LuSmartphone,
  LuWalletMinimal,
  LuZap,
} from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { cn } from '@/core/utils/common'

type PaymentOptionProps = {
  label: string
  method?: string
  brand?: string
  selected: boolean
  disabled: boolean
  onSelect: () => void
}
const LOGOS: Record<string, string> = {
  TOSSPAY: 'tosspay',
  NAVERPAY: 'naverpay',
  KAKAOPAY: 'kakaopay',
}
const ICONS = {
  CARD: LuCreditCard,
  TRANSFER: LuLandmark,
  VIRTUAL_ACCOUNT: LuWalletMinimal,
  MOBILE_PHONE: LuSmartphone,
  BRANDPAY: LuZap,
}

export default function PaymentOption({
  label,
  method = 'CARD',
  brand,
  selected,
  disabled,
  onSelect,
}: PaymentOptionProps) {
  const logo = brand ? LOGOS[brand] : undefined
  const key = brand === 'BRANDPAY' ? brand : method
  const Icon = ICONS[key as keyof typeof ICONS] ?? LuCreditCard
  return (
    <Button
      variant="choice"
      shape="field"
      size="md"
      disabled={disabled}
      aria-pressed={selected}
      aria-label={label}
      className="group w-full"
      onClick={onSelect}
    >
      <span className="flex w-full min-w-0 flex-col gap-2 text-left">
        <span
          className="flex items-center justify-between gap-3"
          aria-hidden="true"
        >
          {logo ? (
            <Image
              src={'/images/veluvit/payments/' + logo + '.webp'}
              alt=""
              width={32}
              height={32}
              className="size-6 flex-none rounded-md object-contain"
            />
          ) : (
            <Icon
              className={cn(
                'size-6 flex-none stroke-[1.5] transition-colors duration-200',
                selected
                  ? 'text-point'
                  : 'text-description group-hover:text-foreground'
              )}
            />
          )}
          <span
            className={cn(
              'flex size-4 flex-none items-center justify-center rounded-full border transition-colors duration-200',
              selected
                ? 'border-point bg-point text-inverse'
                : 'border-foreground/20'
            )}
          >
            {selected && <LuCheck className="size-3 stroke-[2.5]" />}
          </span>
        </span>
        <span className="min-w-0 whitespace-normal break-keep text-c14 font-semibold leading-5 over10:text-c15">
          {label}
        </span>
      </span>
    </Button>
  )
}
